// UserList.tsx - отображение и управление списком пользователей. 
// Позволяет приглашать и удалять пользователей
import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { subtenantApi as api, Subtenant, subtenantApi, User } from "../../../api/subtenantApi";
import { useFetch } from "../../../hooks/useFetch3";
import { usePresetDialogs } from '../../dialog';
import ActionButtons from '../../ActionButtons';
import { FormModal } from '../../modal/FormModal';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Title } from '@mui/icons-material';

export const UserList: React.FC = () => {
    const { t } = useTranslation();
    const { subtenantId, userId } = useAuthStore();
    const { data: userList, update, error, refresh } = useFetch<User[]>(
        useAuthStore.getState().getUserList,
        [],
        {subtenantId},
        [subtenantId]
    );
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    const { showConfirm, showInfo, showPromptExt, DialogComponent } = usePresetDialogs();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

// onlyOwn section
    const [onlyOwn, setOnlyOwn] = useState(!subtenantId);

    const handleOwnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOnlyOwn(event.target.checked);
    };

    const filteredUserList = useMemo(() => {
        return !onlyOwn ? userList : userList.filter(row => !row.subtenant_id);
    }, [userList, onlyOwn]);
//

    const columns: CustomGridColDef[] = [
        { field: "id", headerName: 'ID', type: "number", flex: 1, width: 100 },
        { field: "username", headerName: t('User.username'), type: "string", flex: 2, width: 200 },
        { field: "email", headerName: t('User.email'), type: "string", flex: 3, width: 300 },
        // { field: "subtenant_id", headerName: 'sid', type: "number", flex: 1, width: 50, },
        { field: "subtenant_id", headerName: t('PlatesList.subtenant'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: subtenants.map( subtenant => ({value: subtenant.subtenant_id, label: subtenant.name})),
            hidden: onlyOwn || !!subtenantId,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <Typography>
                        {subtenants.find(s => s.subtenant_id === params.row.subtenant_id)?.name}
                    </Typography>
                );
            }
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<User>) => {
                return (
                    <ActionButtons actions={[
                        { type: 'delete', action: () => handleDelete(params.row.id) },
                    ]} />
                );
            },
        },        
    ];

    const handleDelete = async (id: number) => {
        if (id == userId) {
            await showInfo('Заборонено видаляти власний обліковий запис');
            return;
        }
        const result = await showConfirm(
            t('common.delete_question', { id }),
            t('common.delete_confirmation')
        );
        if (!result) return;

        await api.delete(id);
        const updatedList = userList.filter(user => id !== user.id);
        update(updatedList);
        console.log(`Subtenant with ID ${id} deleted.`);
    };

    const isEmailAccessible = async (email: string) => {
        const res = await useAuthStore.getState().checkEmail(email);
        return res;
    }

    const handleInvite = async () => {
        console.log('handleInvite');
        const email = await showPromptExt(
            'Зазначте e-mail нового користувача',
            '',
            'Запросити користувача',
            isEmailAccessible,
        );
        if (email) {
            await useAuthStore.getState().sendInvitationEmail(subtenantId || -1, email);
            alert(t('SubtenantForm.invitation_sent_to') +' '+ email);
        }
    }

    const handleRefresh = () => {
        refresh();
    }
    
    return (
        <Box sx={{ width: '100%', p: isMobile ? 1 : 0 }}>
            
            <Typography variant="h5" component="h3" align="left" sx={{'p': 2}}>
                Користувачі
            </Typography>

            {DialogComponent}
            
            <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'space-between'} }>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleInvite()}
                >
                    {t('UserList.Invite_new_user')}
                </Button>

                {!subtenantId && (
                <FormControlLabel
                    control={
                        <Checkbox 
                            checked={onlyOwn} 
                            onChange={handleOwnChange} 
                            color="primary"
                        />
                    }
                    label={t('UserList.Only_Own')}
                    sx={{ 
                        whiteSpace: 'nowrap', // Чтобы текст не переносился на новую строку
                        marginLeft: isMobile ? 0 : 1 // Небольшой отступ слева на десктопе
                    }}
                />
                )}
                </Stack>

                <IconButton onClick={handleRefresh}>
                    <RefreshIcon />
                </IconButton>
            </Stack>

            {!isMobile && (
                <LocalizedGrid
                    lang={i18n.language as any}
                    gridId='SubtenantsList'
                    rows={filteredUserList}
                    columns={columns} 
                    getRowId={(row) => row.id}
                    sx={{ maxWidth: '1000px' }}
                    hideToolbar={true}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

        </Box>
    );
}
